import React, { createContext, useState, useEffect, useContext } from "react";
import { authAPI, userAPI } from "../services/api";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem("token"));
  const [loading, setLoading] = useState(true);

  // Load user on mount if token exists
  useEffect(() => {
    const fetchUser = async () => {
      try {
        const response = await userAPI.getCurrentUser();
        setUser(response.data);
      } catch (error) {
        console.error("Failed to load user:", error);
        logout();
      } finally {
        setLoading(false);
      }
    };

    if (token) {
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const login = async (credentials) => {
    try {
      const response = await authAPI.login(credentials);
      const { token, id, username, email, isAdmin } = response.data;

      localStorage.setItem("token", token);
      setToken(token);
      setUser({ id, username, email, isAdmin });

      return { success: true };
    } catch (error) {
      // Handle validation errors (400)
      if (error.response?.status === 400) {
        const validationError =
          error.response?.data?.message ||
          error.response?.data?.error ||
          "Invalid credentials";
        return {
          success: false,
          message: validationError,
        };
      }

      // Handle authentication errors (401)
      if (error.response?.status === 401) {
        return {
          success: false,
          message: "Invalid username/email or password",
        };
      }

      // Handle other errors
      return {
        success: false,
        message:
          error.response?.data?.message || "Login failed. Please try again.",
      };
    }
  };

  const register = async (userData) => {
    try {
      const response = await authAPI.register(userData);
      return {
        success: response.data.success,
        message: response.data.message,
      };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || "Registration failed",
      };
    }
  };

  const logout = () => {
    localStorage.removeItem("token");
    setToken(null);
    setUser(null);
  };

  const value = {
    user,
    token,
    loading,
    login,
    register,
    logout,
    isAuthenticated: !!token,
    isAdmin: user?.isAdmin || false,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};
